import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { publicApi } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import PreviewBanner from "@/components/public/PreviewBanner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageIntro, PAGE_INTROS } from "@/lib/cms";
import { fmtDate } from "@/lib/format";
import { BookOpen, Clock, ArrowRight } from "lucide-react";

function Meta({ a }) {
  return (
    <div className="flex items-center gap-3 text-[11px] text-[var(--muted)] mt-2">
      {a.category && <span className="text-[#c9a86a] tracking-wide uppercase">{a.category}</span>}
      {a.published_at && <span>{fmtDate(a.published_at)}</span>}
      {a.reading_time && <span className="flex items-center gap-1"><Clock size={11} />{a.reading_time} min</span>}
    </div>
  );
}

export default function Journal() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [intro, preview] = usePageIntro(PAGE_INTROS.journal.key, PAGE_INTROS.journal.defaults);

  useEffect(() => {
    publicApi.get("/public/journal/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    try {
      const r = await publicApi.get("/public/journal", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const feature = category === "all" ? items.find((a) => a.featured) : null;
  const rest = feature ? items.filter((a) => a.id !== feature.id) : items;

  return (
    <PublicLayout>
      <title>The Journal · Le Collectif Horloger</title>
      <meta name="description" content="Watch guides, collecting strategy, and brand spotlights from Le Collectif Horloger." />

      <section className="max-w-7xl mx-auto px-5 py-14">
        <div className="text-center mb-10">
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">{intro.eyebrow}</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">{intro.title}</h1>
          <p className="text-[var(--text-2)] mt-3 max-w-xl mx-auto">{intro.subtitle}</p>
        </div>

        <div className="flex justify-center mb-8">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="sm:w-56 bg-[var(--panel)] border-[var(--border-2)] text-[var(--text)]" data-testid="journal-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Topics</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-[var(--panel)] border border-[var(--border-lch)] rounded-lg overflow-hidden">
                <Skeleton className="aspect-[16/10] w-full bg-[var(--panel-2)]" />
                <div className="p-4 space-y-2"><Skeleton className="h-3 w-1/3 bg-[var(--panel-2)]" /><Skeleton className="h-5 w-3/4 bg-[var(--panel-2)]" /></div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-[var(--muted)] py-20" data-testid="journal-empty">No articles published yet.</div>
        ) : (
          <>
            {feature && (
              <button onClick={() => navigate(`/journal/${feature.slug}`)} data-testid={`journal-feature-${feature.id}`}
                className="group w-full text-left grid md:grid-cols-2 gap-0 mb-10 bg-[var(--panel)] border border-[var(--border-lch)] rounded-lg overflow-hidden hover:border-[#c9a86a]/50 transition-colors">
                <div className="aspect-[16/10] md:aspect-auto bg-[var(--panel-2)] overflow-hidden">
                  {feature.cover_image && <img src={feature.cover_image} alt={feature.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="text-[10px] px-2 py-1 rounded-full bg-[#c9a86a] text-[var(--on-gold)] font-semibold tracking-wide w-fit">FEATURED</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-[var(--text)] mt-4">{feature.title}</h2>
                  <p className="text-[var(--text-2)] mt-3 leading-relaxed">{feature.excerpt}</p>
                  <Meta a={feature} />
                  <span className="mt-5 text-[#c9a86a] text-sm flex items-center gap-1 group-hover:gap-2 transition-all">Read article <ArrowRight size={15} /></span>
                </div>
              </button>
            )}

            <div className="grid md:grid-cols-3 gap-6" data-testid="journal-grid">
              {rest.map((a) => (
                <button key={a.id} onClick={() => navigate(`/journal/${a.slug}`)} data-testid={`journal-card-${a.id}`}
                  className="group text-left bg-[var(--panel)] border border-[var(--border-lch)] rounded-lg overflow-hidden hover:border-[#c9a86a]/50 transition-colors flex flex-col">
                  <div className="aspect-[16/10] bg-[var(--panel-2)] overflow-hidden">
                    {a.cover_image ? (
                      <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><BookOpen size={32} /></div>}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-lg font-medium text-[var(--text)] leading-snug">{a.title}</h3>
                    <p className="text-sm text-[var(--muted)] mt-2 line-clamp-3 flex-1">{a.excerpt}</p>
                    <Meta a={a} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>
      {preview && <PreviewBanner testId="journal-preview-banner" />}
    </PublicLayout>
  );
}
